import { ReactNode, useEffect, useState } from "react";
import type { Message } from "@ag-ui/core";
import { AlertCircle } from "lucide-react";
import { AssistantTurn } from "./AssistantTurn";
import { ConversationProvider } from "./ConversationContext";
import { UserMessage } from "./UserMessage";
import { useTurns } from "./useTurns";

export interface ReadOnlyConversationData {
  display_name: string;
  owner_display_name: string | null;
  created_at: string;
  messages: Message[];
}

export interface ReadOnlyConversationViewProps {
  conversationId: string | null;
  // Set on share-token views so artifact renderers can hit the token-scoped
  // previews endpoint. Exactly one of conversationId / shareToken is set.
  shareToken?: string | null;
  fetcher: (signal: AbortSignal) => Promise<ReadOnlyConversationData | null>;
  bannerSubtitle?: string;
  // When set, replaces the entire subtitle line (ownerVerb + owner + bannerSubtitle).
  bannerSubtitleContent?: string;
  ownerVerb?: string;
  notFoundTitle?: string;
  notFoundBody?: string;
  rightPanel?: ReactNode;
  readOnlyFeedback?: boolean;
}

export const ReadOnlyConversationView = ({
  conversationId,
  shareToken = null,
  fetcher,
  bannerSubtitle = "Read-only view.",
  bannerSubtitleContent,
  ownerVerb = "Shared by",
  notFoundTitle = "This conversation isn't available",
  notFoundBody = "It may have been removed, or it never existed.",
  rightPanel,
  readOnlyFeedback = false,
}: ReadOnlyConversationViewProps) => {
  const [view, setView] = useState<ReadOnlyConversationData | null | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const next = await fetcher(controller.signal);
        if (controller.signal.aborted) return;
        setView(next);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err);
      }
    })();
    return () => controller.abort();
  }, [fetcher]);

  if (error) {
    return (
      <EmptyState
        title="Couldn't load conversation"
        body="Something went wrong reaching the server. Try refreshing in a moment."
      />
    );
  }

  if (view === undefined) {
    return (
      <div
        className="flex flex-1 items-center justify-center"
        role="status"
        aria-busy="true"
        aria-label="Loading conversation"
      />
    );
  }

  if (view === null) {
    return <EmptyState title={notFoundTitle} body={notFoundBody} />;
  }

  return (
    <ConversationProvider conversationId={conversationId} shareToken={shareToken}>
      <div className="flex h-full overflow-hidden">
        <ReadOnlyConversationBody
          view={view}
          conversationId={conversationId}
          bannerSubtitle={bannerSubtitle}
          bannerSubtitleContent={bannerSubtitleContent}
          ownerVerb={ownerVerb}
          readOnlyFeedback={readOnlyFeedback}
        />
        {rightPanel}
      </div>
    </ConversationProvider>
  );
};

interface ReadOnlyConversationBodyProps {
  view: ReadOnlyConversationData;
  conversationId: string | null;
  bannerSubtitle: string;
  bannerSubtitleContent?: string;
  ownerVerb: string;
  readOnlyFeedback: boolean;
}

const ReadOnlyConversationBody = ({
  view,
  conversationId,
  bannerSubtitle,
  bannerSubtitleContent,
  ownerVerb,
  readOnlyFeedback,
}: ReadOnlyConversationBodyProps) => {
  const turns = useTurns(view.messages, false);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto pt-6">
        <div className="mx-auto flex max-w-[60rem] flex-col gap-4 px-6 pb-8">
          <header className="rounded-xl border border-border bg-muted/30 px-4 py-3">
            <h1 className="truncate text-base font-medium text-foreground">{view.display_name}</h1>
            <p className="mt-1 text-xs text-muted-foreground">
              {bannerSubtitleContent ??
                `${view.owner_display_name ? `${ownerVerb} ${view.owner_display_name} · ` : ""}${bannerSubtitle}`}
            </p>
          </header>
          <ol className="flex flex-col gap-4" role="log">
            {turns.map((turn) =>
              turn.kind === "user" ? (
                <UserMessage key={turn.key} text={turn.text} />
              ) : (
                <AssistantTurn
                  key={turn.key}
                  turn={turn}
                  agent={null}
                  conversationId={conversationId ?? ""}
                  readOnlyFeedback={readOnlyFeedback}
                />
              )
            )}
          </ol>
        </div>
      </div>
    </div>
  );
};

interface EmptyStateProps {
  title: string;
  body: string;
}

const EmptyState = ({ title, body }: EmptyStateProps) => (
  <div
    role="alert"
    className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-muted-foreground"
  >
    <AlertCircle className="size-8" aria-hidden="true" />
    <p className="text-sm font-medium text-foreground">{title}</p>
    <p className="text-sm">{body}</p>
  </div>
);
